import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {SegmentsClientService} from './segments-client.service';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  first,
  map,
  mergeMap,
  switchMap,
  tap
} from 'rxjs/operators';
import {
  allSegmentsForTaxonomyAttachmentLoaded,
  attachSegmentsToTaxonomy,
  closeSegmentDetails,
  closeSegmentDetailsAndClearSelection,
  deleteSegment,
  editSegment,
  loadAllSegmentsForTaxonomyAttachment,
  loadParentSegments,
  loadSegmentDetails,
  loadSegments,
  loadSegmentsForExport,
  loadSegmentsForExportPreview,
  loadSegmentsForTaxonomy,
  multishareComplete,
  parentSegmentsLoaded,
  reloadSegmentList,
  reloadSegmentListWithProps,
  segmentCreated,
  segmentDeleted,
  segmentDeletionError,
  segmentDetailsLoaded,
  segmentEdited,
  segmentEditError,
  segmentsAttached,
  segmentsForExportLoaded,
  segmentsForExportPreviewLoaded,
  segmentsForTaxonomyLoaded,
  segmentsLoaded,
  selectionChanged,
  selectSegmentById,
  selectSegmentDetails
} from './segments.state';
import {ApiVersion, errorHappened, successHappened, userSet} from '../app.types';
import {of} from 'rxjs';
import {Store} from '@ngrx/store';
import {selectPartnerId} from '../user/user.reducer';
import {ExportPreparerService} from './list/export-preparer.service';
import {HttpInteraction} from '../utils/http-interactions.utils';
import {targetUnshared} from '../target/target.state';
import {ParentSegmentsStateProxyService} from './parent-segments-state-proxy.service';


@Injectable()
export class SegmentsEffects {

  private static limitlessPageSortedById = {
    page: 0,
    size: 10000000,
    sort: {
      sortField: 'id',
      direction: 'DESC'
    }
  };

  loadModel$ = createEffect(() => this.actions$
    .pipe(
      ofType(loadSegments),
      debounceTime(100),
      switchMap((value) =>
        this.store
          .select(selectPartnerId)
          .pipe(
            first(),
            switchMap(partnerId =>
              this.client
                .getSegmentsByFilter({...value.filter, partnerId}, value.page, value.apiVersion)
                .pipe(
                  map(result => segmentsLoaded({segments: result})),
                  catchError(err => of(errorHappened({error: {message: err.message}})))
                )))
      )
    ));
  selectionChanged$ = createEffect(() => this.actions$
    .pipe(
      ofType(selectionChanged),
      mergeMap((event) => {
          if (event.selectedIds.length === 1) {
            return of(loadSegmentDetails({segmentId: event.selectedIds[0]}));
          }
          return of(closeSegmentDetails());
        }
      )
    ));
  closeSegmentDetails$ = createEffect(() => this.actions$
    .pipe(
      ofType(userSet, segmentDeleted),
      mergeMap((_) => of(closeSegmentDetailsAndClearSelection()))
    ));
  loadSegmentDetails$ = createEffect(() => this.actions$
    .pipe(
      ofType(loadSegmentDetails),
      switchMap((event) => this.store
        .select(selectSegmentById(String(event.segmentId)))
        .pipe(
          first(),
          switchMap(segment =>
            this.client
              .getSharedAccountsWithSegment(segment.id)
              .pipe(
                map(result => segmentDetailsLoaded({segmentDetails: {segment, accounts: result}})),
                catchError(err => of(errorHappened({error: {message: err.message}})))
              )))
      )
    ));
  editSegment$ = createEffect(() => this.actions$
    .pipe(
      ofType(editSegment),
      switchMap((editEvent) => this.store
        .select(selectPartnerId)
        .pipe(
          first(),
          switchMap(partnerId =>
            this.client
              .editSegment({
                ...editEvent.segment,
                partnerId: editEvent.makeGlobal ? 0 : partnerId
              }, editEvent.apiVersion)
              .pipe(
                map(result => segmentEdited({segment: result})),
              )),
          catchError(err => of(segmentEditError({error: HttpInteraction.errorModel(err)})))
        )
      )
    ));
  deleteSegment$ = createEffect(() => this.actions$
    .pipe(
      ofType(deleteSegment),
      switchMap((deleteEvent) =>
        this.client
          .deleteSegment(deleteEvent.segment)
          .pipe(
            map(result => segmentDeleted({segment: deleteEvent.segment})),
            catchError(err => of(segmentDeletionError({error: HttpInteraction.errorModel(err)})))
          )
      )
    ));
  notifySuccess$ = createEffect(() => this.actions$
    .pipe(
      ofType(segmentEdited, segmentDeleted, segmentCreated),
      mergeMap((event) => of(successHappened({message: `${event.segment.name} state successfully changed.`}))
      )
    ));
  reloadList$ = createEffect(() => this.actions$
    .pipe(
      ofType(segmentEdited, segmentCreated, targetUnshared, multishareComplete),
      switchMap((_) => of(reloadSegmentList())
      )
    ));
  reloadListWhenPartnerIdChanges$ = createEffect(() => this.store
    .select(selectPartnerId)
    .pipe(
      distinctUntilChanged(),
      map(_ => reloadSegmentList())));
  reloadListAfterDelete$ = createEffect(() => this.actions$
    .pipe(
      ofType(segmentDeleted),
      switchMap((_) => of(reloadSegmentListWithProps({props: 'clear-selection'}))
      )
    ));
  reloadListWithProps$ = createEffect(() => this.actions$
    .pipe(
      ofType(reloadSegmentList),
      debounceTime(50),
      switchMap((_) => of(reloadSegmentListWithProps({}))
      )
    ));
  reloadSegmentDetails$ = createEffect(() => this.actions$
    .pipe(
      ofType(segmentEdited, targetUnshared),
      switchMap((event) => of(loadSegmentDetails({segmentId: event.segment.id}))
      )
    ));
  reloadSegmentDetailsAfterMultishare$ = createEffect(() => this.actions$
    .pipe(
      ofType(multishareComplete),
      switchMap((multishareEvent) => this.store
        .select(selectSegmentDetails)
        .pipe(
          filter(details => details?.segment != null && multishareEvent.segmentIds.includes(details.segment.id)),
          first(),
          switchMap(segDetails => {
            if (!segDetails?.segment) {
              return of(errorHappened({error: {message: 'This should not happen!'}}));
            } else {
              return of(loadSegmentDetails({segmentId: segDetails?.segment.id}));
            }
          }))
      )
    ));
  /**
   * load all segments that we have, filtered by current filter
   */
  loadModelForExportPreview$ = createEffect(() => this.actions$
    .pipe(
      ofType(loadSegmentsForExportPreview),
      mergeMap((event) =>
        this.store
          .select(selectPartnerId)
          .pipe(
            switchMap(partnerId =>
              this.client
                .getSegmentsByFilter({
                    ...event.filter,
                    partnerId
                  },
                  {
                    page: 0,
                    size: 50,
                    sort: {
                      sortField: 'id',
                      direction: 'DESC'
                    }
                  },
                  ApiVersion.V1)
                .pipe(
                  map(result => segmentsForExportPreviewLoaded({
                    segments: result,
                    filterSearchValues: event.filterSearchValues,
                    apiVersion: event.apiVersion
                  })),
                  catchError(err => of(errorHappened({error: {message: err.message}})))
                )))
      )
    ));
  loadModelForExport$ = createEffect(() => this.actions$
    .pipe(
      ofType(loadSegmentsForExport),
      mergeMap((event) =>
        this.store
          .select(selectPartnerId)
          .pipe(
            switchMap(partnerId =>
              this.client
                .getSegmentsByFilter({
                    ...event.filter,
                    partnerId
                  },
                  SegmentsEffects.limitlessPageSortedById,
                  ApiVersion.V1)
                .pipe(
                  map(result => segmentsForExportLoaded({
                    segments: result,
                    filterSearchValues: event.filterSearchValues
                  })),
                  catchError(err => of(errorHappened({error: {message: err.message}})))
                )))
      )
    ));
  exportPreviewLoaded$ = createEffect(() => this.actions$
    .pipe(
      ofType(segmentsForExportPreviewLoaded),
      tap((event) =>
        this.exportPreparer.openDialog(event.segments, event.filterSearchValues, event.apiVersion)
      )
    ), {dispatch: false});

  exportLoaded$ = createEffect(() => this.actions$
    .pipe(
      ofType(segmentsForExportLoaded),
      tap((event) =>
        this.exportPreparer.notifyExportValues(event.segments, event.filterSearchValues)
      )
    ), {dispatch: false});
  parentSegmentsLoaded$ = createEffect(() => this.actions$
    .pipe(
      ofType(parentSegmentsLoaded),
      tap((event) =>
        this.parentSegmentsStateProxy.candidatesChanged(event.segments.content)
      )
    ), {dispatch: false});
  loadParentSegmentsCandidatesForCreationOrEdit$ = createEffect(() => this.actions$
    .pipe(
      ofType(loadParentSegments),
      mergeMap((event) =>
        this.store
          .select(selectPartnerId)
          .pipe(
            switchMap(partnerId =>
              this.client
                .getSegmentsByFilter({
                    ...event.filter,
                    partnerId
                  },
                  SegmentsEffects.limitlessPageSortedById,
                  ApiVersion.V2)
                .pipe(
                  map(result => parentSegmentsLoaded({segments: result})),
                  catchError(err => of(errorHappened({error: {message: err.message}})))
                )))
      )
    ));

  loadAllSegmentsForTaxonomyAttachment$ = createEffect(() => this.actions$
    .pipe(
      ofType(loadAllSegmentsForTaxonomyAttachment),
      mergeMap((event) =>
        this.store
          .select(selectPartnerId)
          .pipe(
            switchMap(partnerId =>
              this.client
                .getSegmentsByFilter({
                  ...event.filter,
                  partnerId
                }, SegmentsEffects.limitlessPageSortedById, ApiVersion.V1)
                .pipe(
                  map(result => allSegmentsForTaxonomyAttachmentLoaded({segments: result.content})),
                  catchError(err => of(errorHappened({error: {message: err.message}})))
                )))
      )
    ));

  attachSegmentsToTaxonomy = createEffect(() => this.actions$
    .pipe(
      ofType(attachSegmentsToTaxonomy),
      mergeMap((event) =>
        this.client
          .attachSegmentsToTaxonomy(event.segmentIds, event.taxonomyId)
          .pipe(
            map(() => segmentsAttached()),
            catchError(err => of(errorHappened({error: {message: err.message}})))
          ))
    )
  );
  segmentsAttachedNotifySuccess = createEffect(() => this.actions$
    .pipe(
      ofType(segmentsAttached),
      map((event) => successHappened({message: 'Successfully attached segments to taxonomy!'})
      ))
  );

  loadSegmentsForTaxonomy = createEffect(() => this.actions$
    .pipe(
      ofType(loadSegmentsForTaxonomy),
      mergeMap((event) =>
        this.store
          .select(selectPartnerId)
          .pipe(
            switchMap(partnerId =>
              this.client
                .getSegmentsByFilter({
                  ...event.filter,
                  partnerId
                }, SegmentsEffects.limitlessPageSortedById, ApiVersion.V2)
                .pipe(
                  map(result => segmentsForTaxonomyLoaded({segments: result.content, taxonomyId: event.taxonomyId})),
                  catchError(err => of(errorHappened({error: {message: err.message}})))
                )))
      )
    ));

  constructor(private actions$: Actions,
              private client: SegmentsClientService,
              private exportPreparer: ExportPreparerService,
              private parentSegmentsStateProxy: ParentSegmentsStateProxyService,
              private store: Store) {
  }
}
